import React, { useEffect, useState } from "react";
import { Avatar, Dropdown, Card, Badge, Button, Modal, TextInput, Label, Spinner } from "flowbite-react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tag } from "../";
import { timestampToDateString } from "../../config/firebase";
import {
  updateUserRole,
  fetchUserAmbassadorStats,
  assignUserAsAmbassador,
  removeUserAsAmbassador,
} from "../../redux/slices/users";
import { capitalize } from "../../helpers/handlers/methods";

const formatKobo = (kobo) => {
  const naira = (kobo || 0) / 100;
  return `₦${naira.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const UserProfile = ({ user, loading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const { applications, applicationsLoading, ambassadorStats, ambassadorStatsLoading, ambassadorActionLoading } =
    useSelector((state) => state.users);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [customCode, setCustomCode] = useState("");
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const isAdmin = loggedInUser.userType === "admin";

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserAmbassadorStats({ userId: user.id }));
    }
  }, [user?.id, dispatch]);

  const handleUserRoleChange = async (role) => {
    try {
      await dispatch(updateUserRole({ userId: user.id, role }));
    } catch {}
  };

  const handleAssign = () => {
    dispatch(
      assignUserAsAmbassador({
        userId: user.id,
        customCode: customCode.trim() || undefined,
      })
    ).then((res) => {
      if (!res.error) {
        setShowAssignModal(false);
        setCustomCode("");
        dispatch(fetchUserAmbassadorStats({ userId: user.id }));
      }
    });
  };

  const handleRemove = () => {
    dispatch(removeUserAsAmbassador({ userId: user.id })).then((res) => {
      if (!res.error) setShowRemoveConfirm(false);
    });
  };

  return (
    <>
      {loading ? (
        <div role="status" className="animate-pulse">
          <div className="grid grid-cols-3 gap-4 mb-4 mt-4 flex">
            <div className="shadow rounded-lg border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 content-center relative py-8 bg-gray-200 rounded-full dark:bg-gray-700">
              <Avatar alt="User profile" rounded size="xl" />
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-4"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-4"></div>
            </div>
            <div className="shadow rounded-lg border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 content-start relative col-span-2 py-8">
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 w-32"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-4 w-60"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-8 w-32"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-4 w-60"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-8 w-32"></div>
              <div className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 mx-20 mt-4 w-60"></div>
            </div>
          </div>
          <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <>
          {/* Main info card */}
          <div className="grid grid-cols-3 gap-4 mb-4 mt-4 flex">
            <div className="shadow rounded-lg border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 content-center relative py-8">
              <Avatar alt="User profile" img={user && user.profileImage} rounded size="xl" />
              <center>
                <div className="text-2xl font-display font-semibold mt-2 dark:text-white">
                  {user && user.fullName}
                </div>
                <div className="text-lg text-gray-600 mt-2 dark:text-gray-300">
                  Member since: {user && timestampToDateString(user.createdAt)}
                </div>
              </center>
            </div>
            <div className="shadow rounded-lg border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 content-center relative col-span-2 p-6 content-start">
              <div className="flex flex-col mb-4">
                <span className="text-black dark:text-white font-medium">Email Address</span>
                <span className="text-gray-600 dark:text-gray-300">
                  <a href={`mailto:${user.email}`} className="hover:font-medium text-purple-500 underline">
                    {user.email}
                  </a>
                </span>
              </div>
              <div className="flex flex-col mb-4">
                <span className="text-black dark:text-white font-medium">Phone Number</span>
                <span className="text-gray-600 dark:text-gray-300">{user.phoneNumber || "xxxxxxxxxx"}</span>
              </div>
              <div className="flex flex-col mb-4">
                <span className="text-black dark:text-white font-medium">Role</span>
                <span className="text-gray-600 dark:text-gray-300">
                  {isAdmin && loggedInUser.email !== user.email && user.email !== "admin@purplebook.ng" ? (
                    <Dropdown label={capitalize(user.userType)} size="sm" color="purple">
                      <Dropdown.Item onClick={() => handleUserRoleChange("admin")}>Admin</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUserRoleChange("employee")}>Employee</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUserRoleChange("user")}>User</Dropdown.Item>
                    </Dropdown>
                  ) : (
                    <Tag text={user.userType} type={user.userType} />
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Ambassador Section */}
          <div className="mt-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold dark:text-white">Creator</h3>
              {ambassadorStatsLoading && <Spinner size="sm" />}
            </div>

            {!ambassadorStatsLoading && ambassadorStats && (
              <div className="shadow rounded-lg bg-white dark:bg-gray-800 p-6">
                {/* Status row */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Badge color={ambassadorStats.isAmbassador ? "purple" : "gray"} size="sm">
                      {ambassadorStats.isAmbassador ? "Creator" : "Not a Creator"}
                    </Badge>
                    {ambassadorStats.codes.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {ambassadorStats.codes.map((c) => (
                          <span
                            key={c.code}
                            className={`font-mono text-sm px-2 py-0.5 rounded ${
                              c.isActive
                                ? "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200"
                                : "bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 line-through"
                            }`}
                            title={c.isActive ? "Active" : "Inactive"}
                          >
                            {c.code}
                            <span className="ml-1 text-xs opacity-60">({c.usageCount} uses)</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div>
                      {ambassadorStats.isAmbassador ? (
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => setShowRemoveConfirm(true)}
                          disabled={ambassadorActionLoading}
                        >
                          Remove Creator
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          color="purple"
                          onClick={() => setShowAssignModal(true)}
                          disabled={ambassadorActionLoading}
                        >
                          Make Creator
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Stats grid */}
                {ambassadorStats.isAmbassador && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { label: "Signups", value: ambassadorStats.totalSignups ?? "—", title: "Verified users who signed up with this code" },
                      { label: "Commissions", value: ambassadorStats.totalCommissions, title: "Payment events that earned a commission" },
                      { label: "Total Earned", value: formatKobo(ambassadorStats.totalRewardEarned) },
                      { label: "Pending Payout", value: formatKobo(ambassadorStats.totalRewardPending) },
                      { label: "Paid Out", value: formatKobo(ambassadorStats.totalRewardPaid) },
                    ].map(({ label, value, title }) => (
                      <div key={label} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center" title={title}>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                          {label}
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">{value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Applications Section */}
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4 dark:text-white">
              Applications ({applications.length})
            </h3>
            {applicationsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded-lg dark:bg-gray-700" />
                ))}
              </div>
            ) : applications.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {applications.map((application) => (
                  <Card
                    key={application.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate(`/applications/${application.id}`)}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold dark:text-white">
                          {application.service?.title || "Application"}
                        </h4>
                        <Badge
                          color={
                            application.status === "done"
                              ? "success"
                              : application.status === "in_progress"
                              ? "warning"
                              : application.status === "closed"
                              ? "failure"
                              : "info"
                          }
                        >
                          {capitalize(application.status?.replace(/_/g, " ") || "pending")}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Created: {timestampToDateString(application.createdAt)}
                      </p>
                      {application.assignee && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Assignee: {application.assignee.fullName}
                        </p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">No applications found for this user.</p>
            )}
          </div>

          {/* Assign Ambassador Modal */}
          <Modal show={showAssignModal} onClose={() => { setShowAssignModal(false); setCustomCode(""); }}>
            <Modal.Header>Make Creator</Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  This will grant <strong>{user.fullName}</strong> creator status and create a referral code for them.
                </p>
                <div>
                  <Label htmlFor="customCode">Custom Code (optional)</Label>
                  <TextInput
                    id="customCode"
                    placeholder="e.g. EMMA or EMMA-VIP — leave blank to auto-generate"
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                    maxLength={30}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Letters, numbers and hyphens only. If blank, a code like <span className="font-mono">EMMA-A3B5C9D2</span> is auto-generated from their name.
                  </p>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button color="purple" onClick={handleAssign} disabled={ambassadorActionLoading}>
                {ambassadorActionLoading ? <Spinner size="sm" /> : "Assign"}
              </Button>
              <Button color="gray" onClick={() => { setShowAssignModal(false); setCustomCode(""); }}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Remove Confirm Modal */}
          <Modal show={showRemoveConfirm} onClose={() => setShowRemoveConfirm(false)}>
            <Modal.Header>Remove Creator Status</Modal.Header>
            <Modal.Body>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                This will remove the creator role from <strong>{user.fullName}</strong> and deactivate all their referral codes. Their existing earned rewards are not affected.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button color="failure" onClick={handleRemove} disabled={ambassadorActionLoading}>
                {ambassadorActionLoading ? <Spinner size="sm" /> : "Remove"}
              </Button>
              <Button color="gray" onClick={() => setShowRemoveConfirm(false)}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default UserProfile;
